<template>
  <div class="new-page">

    <!-- Gönderildi -->
    <section class="done" v-if="sentLinks">
      <span class="done-icon"><i class="fa fa-paper-plane-o"></i></span>
      <h2>{{ $t('newEnvelope.sent') }}</h2>
      <p class="done-lead">{{ sentMessage }}</p>

      <div class="link-box" v-for="link in sentLinks" :key="link.email">
        <div class="link-head">
          <div class="link-who">
            <span class="row-mark">{{ personInitials(link.name || link.email) }}</span>
            <div>
              <strong>{{ link.name }}</strong>
              <span class="row-sub">{{ link.email }}</span>
            </div>
          </div>
          <span class="pill" :class="link.sent ? 'is-ok' : 'is-warn'">
            {{ link.sent ? $t('envelopes.mailSent') : $t('envelopes.mailFailed') }}
          </span>
        </div>
        <p class="link-hint" v-if="!link.sent">{{ $t('newEnvelope.linkNote') }}</p>
        <a class="link-text" :href="link.url" target="_blank">{{ link.url }}</a>
      </div>

      <div class="actions center">
        <button class="btn-primary" @click="reset">{{ $t('newEnvelope.newPackage') }}</button>
        <button class="btn-ghost" @click="$emit('sent')">{{ $t('nav.backToList') }}</button>
      </div>
    </section>

    <template v-else>
      <div class="flash is-err" v-if="errorMessage">
        <span>{{ errorMessage }}</span>
        <button class="flash-close" @click="errorMessage = ''">&times;</button>
      </div>

      <!-- 1. Belge -->
      <section class="step">
        <div class="step-head">
          <span class="step-no">1</span>
          <h3>{{ $t('newEnvelope.stepDocument') }}</h3>
        </div>

        <!-- Üç kaynak: sekme yerine kart, her birinin ne olduğu yazılı -->
        <div class="choices">
          <button v-for="option in sourceOptions" :key="option.value"
                  class="choice" :class="{ 'is-active': mode === option.value }"
                  @click="mode = option.value">
            <span class="choice-icon"><i class="fa" :class="option.icon"></i></span>
            <strong>{{ $t('newEnvelope.' + option.labelKey) }}</strong>
            <span class="choice-hint">{{ $t('newEnvelope.' + option.hintKey) }}</span>
          </button>
        </div>

        <!-- Şablondan -->
        <div class="step-body" v-if="mode === 'template'">
          <div class="select-wrap">
            <select class="select-input" v-model="templateId" @change="onTemplatePicked">
              <option value="">{{ $t('newEnvelope.pickTemplate') }}</option>
              <optgroup :label="$t('newEnvelope.groupActive')" v-if="activeTemplates.length">
                <option v-for="t in activeTemplates" :value="t._id" :key="t._id">
                  {{ t.name }}<span v-if="t.version > 1"> (v{{ t.version }})</span>
                </option>
              </optgroup>
              <optgroup :label="$t('newEnvelope.groupUnusable')" v-if="inactiveTemplates.length">
                <option v-for="t in inactiveTemplates" :value="t._id" :key="t._id" disabled>
                  {{ t.name }} — {{ t.status === 'draft' ? $t('templates.statusDraft') : $t('templates.statusArchived') }}
                </option>
              </optgroup>
            </select>
          </div>

          <p class="hint" v-if="templatesLoading">{{ $t('templates.loadingList') }}</p>
          <p class="note is-warn" v-else-if="!templates.length">{{ $t('newEnvelope.noTemplates') }}</p>
          <p class="note is-warn" v-else-if="!activeTemplates.length">
            {{ $t('newEnvelope.noneActive', { count: templates.length }) }}
          </p>

          <div class="picked" v-if="selectedTemplate">
            <p class="picked-desc">{{ selectedTemplate.description || $t('newEnvelope.noDescription') }}</p>
            <p class="picked-roles" v-if="templateRoles.length">
              {{ $t('newEnvelope.parties') }}:
              <span class="tagline" v-for="r in templateRoles" :key="r.key">{{ r.name }}</span>
            </p>
          </div>
        </div>

        <!-- Dosya yükle -->
        <div class="step-body" v-else-if="mode === 'upload'">
          <div class="drop" :class="{ 'has-file': !!file }" @click="$refs.fileInput.click()">
            <input class="hidden-input" type="file" accept=".pdf,.docx" ref="fileInput" @change="onFilePicked">
            <span class="drop-icon">
              <i class="fa" :class="file ? 'fa-file-pdf-o' : 'fa-cloud-upload'"></i>
            </span>
            <p class="drop-text" v-if="!file">{{ $t('templates.uploadPick') }}</p>
            <p class="drop-text" v-else><strong>{{ file.name }}</strong></p>
          </div>
          <p class="hint">{{ $t('newEnvelope.uploadHelp') }}</p>
        </div>

        <!-- Sıfırdan -->
        <div class="step-body" v-else>
          <textarea class="input is-code" rows="9" v-model="html"
                    :placeholder="$t('templates.contentPlaceholder')"></textarea>
        </div>

        <!-- Şablon olarak kaydet -->
        <label class="switch" v-if="mode !== 'template'">
          <input type="checkbox" v-model="saveAsTemplate">
          <span class="switch-box"></span>
          <span>{{ $t('newEnvelope.saveAsTemplate') }}</span>
        </label>
        <input class="input is-small" v-if="saveAsTemplate && mode !== 'template'"
               v-model.trim="templateName"
               :placeholder="$t('newEnvelope.templateNamePlaceholder')">
      </section>

      <!-- 2. Detaylar -->
      <section class="step">
        <div class="step-head">
          <span class="step-no">2</span>
          <h3>{{ $t('newEnvelope.stepDetails') }}</h3>
        </div>

        <div class="step-body">
          <div class="field">
            <label>{{ $t('newEnvelope.subject') }} <em>*</em></label>
            <input class="input" v-model.trim="subject" maxlength="200"
                   :placeholder="$t('newEnvelope.subjectPlaceholder')">
          </div>
          <div class="field">
            <label>{{ $t('newEnvelope.message') }}</label>
            <textarea class="input is-area" rows="2" v-model.trim="message" maxlength="2000"
                      :placeholder="$t('newEnvelope.messagePlaceholder')"></textarea>
          </div>
        </div>
      </section>

      <!-- 3. Alıcılar -->
      <section class="step">
        <div class="step-head">
          <span class="step-no">3</span>
          <h3>{{ $t('newEnvelope.stepRecipients') }}</h3>
          <button class="btn-ghost is-mini" @click="addRecipient" v-if="!lockedRoles">
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t('newEnvelope.addRecipient') }}</span>
          </button>
        </div>

        <p class="hint mb">{{ lockedRoles ? $t('newEnvelope.lockedRoles') : $t('newEnvelope.orderNote') }}</p>

        <ul class="people">
          <li class="person" v-for="(r, i) in recipients" :key="i">
            <span class="person-no" :title="$t('newEnvelope.order')">{{ i + 1 }}</span>

            <div class="person-fields">
              <span class="person-role" v-if="lockedRoles">{{ r.roleLabel }}</span>
              <input class="input" v-model.trim="r.name" :placeholder="$t('organization.inviteName')">
              <input class="input" type="email" v-model.trim="r.email" placeholder="name@example.com">
            </div>

            <button class="icon-btn is-danger" v-if="!lockedRoles"
                    :disabled="recipients.length === 1" @click="recipients.splice(i, 1)">
              <i class="fa fa-times"></i>
            </button>
          </li>
        </ul>
      </section>

      <!-- Eylemler -->
      <div class="bar">
        <button class="btn-primary" :class="{ 'is-loading': saving }" @click="createAndSend">
          <span class="icon"><i class="fa fa-paper-plane-o"></i></span>
          <span>{{ $t('newEnvelope.createAndSend') }}</span>
        </button>
        <button class="btn-ghost" :class="{ 'is-loading': saving }" @click="createOnly">
          {{ $t('newEnvelope.saveDraft') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import api from '../config/api';

export default {
  name: 'NewEnvelope',
  data() {
    return {
      // Kaynak seçenekleri veriden: yeni bir kaynak eklemek tek satır
      sourceOptions: [
        { value: 'template', icon: 'fa-files-o', labelKey: 'tabTemplate', hintKey: 'tabTemplateHint' },
        { value: 'upload', icon: 'fa-cloud-upload', labelKey: 'tabUpload', hintKey: 'tabUploadHint' },
        { value: 'blank', icon: 'fa-pencil-square-o', labelKey: 'tabBlank', hintKey: 'tabBlankHint' }
      ],

      mode: 'template',
      templates: [],
      templatesLoading: true,
      templateId: '',

      file: null,
      html: '',
      saveAsTemplate: false,
      templateName: '',

      subject: '',
      message: '',
      recipients: [{ name: '', email: '', roleKey: '', roleLabel: '' }],

      saving: false,
      errorMessage: '',
      sentLinks: null,
      sentMessage: '',

      // Vue 2 attribute içinde {{ }} ayrıştıramıyor, metin burada duruyor
      htmlPlaceholder: ''
    };
  },
  computed: {
    selectedTemplate() {
      return this.templates.find((t) => t._id === this.templateId) || null;
    },
    // Aktif olmayanları da gösteriyoruz ama seçtirmiyoruz:
    // boş bir liste "hiç şablonum yok mu?" diye düşündürüyordu
    activeTemplates() {
      return this.templates.filter((t) => t.status === 'active');
    },
    inactiveTemplates() {
      return this.templates.filter((t) => t.status !== 'active');
    },
    templateRoles() {
      return this.selectedTemplate ? (this.selectedTemplate.roles || []) : [];
    },
    // Şablon seçiliyse taraf sayısı ve isimleri şablondan gelir
    lockedRoles() {
      return this.mode === 'template' && this.templateRoles.length > 0;
    }
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    personInitials(source) {
      let text = String(source || '');
      const at = text.indexOf('@');
      if (at > 0) text = text.slice(0, at);
      const parts = text.trim().split(/[\s._-]+/).filter(Boolean);
      if (!parts.length) return '?';
      const locale = this.$locale === 'tr' ? 'tr-TR' : 'en-US';
      const take = parts.length === 1
        ? parts[0].slice(0, 2)
        : parts[0][0] + parts[parts.length - 1][0];
      return take.toLocaleUpperCase(locale);
    },

    loadTemplates() {
      this.templatesLoading = true;
      // Durum filtresi yok: taslakları da çekip nedenini gösterebilmek için
      api.get('/templates', { params: { max: 100 } })
        .then((body) => { this.templates = body.templateList || []; })
        .catch((err) => { this.errorMessage = this.$t('templates.title') + ': ' + err.message; })
        .then(() => { this.templatesLoading = false; });
    },

    onTemplatePicked() {
      const roles = this.templateRoles;
      if (!roles.length) {
        this.recipients = [{ name: '', email: '', roleKey: '', roleLabel: '' }];
        return;
      }
      // Şablonun her tarafı için bir satır aç, sırayı koru
      this.recipients = roles
        .slice()
        .sort((a, b) => (a.order || 1) - (b.order || 1))
        .map((role) => ({ name: '', email: '', roleKey: role.key, roleLabel: role.name }));

      if (!this.subject && this.selectedTemplate) {
        this.subject = this.selectedTemplate.name;
      }
    },

    onFilePicked(event) {
      const picked = event.target.files[0];
      if (!picked) return;
      if (picked.size > 20 * 1024 * 1024) {
        this.errorMessage = this.$t('templates.uploadTooBig');
        return;
      }
      this.file = picked;
      if (!this.subject) this.subject = picked.name.replace(/\.[^.]+$/, '');
    },

    addRecipient() {
      this.recipients.push({ name: '', email: '', roleKey: '', roleLabel: '' });
    },

    validate() {
      if (!this.subject) return this.$t('newEnvelope.errSubject');
      if (this.mode === 'template' && !this.templateId) return this.$t('newEnvelope.errTemplate');
      if (this.mode === 'upload' && !this.file) return this.$t('newEnvelope.errFile');
      if (this.mode === 'blank' && !this.html.trim()) return this.$t('newEnvelope.errContent');

      const bad = this.recipients.find(
        (r) => !r.name || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email)
      );
      if (bad) return this.$t('newEnvelope.errRecipient');

      const emails = this.recipients.map((r) => r.email.toLowerCase());
      if (new Set(emails).size !== emails.length) return this.$t('newEnvelope.errDuplicate');
      return '';
    },

    // Şablon modunda JSON, yükleme modunda multipart gider
    create() {
      const payload = {
        subject: this.subject,
        message: this.message,
        // İmza sayfası bu dilde açılacak. Alıcının tarayıcı dili farklı
        // olabilir; ona anlamlı olan, belgenin gönderildiği dil.
        locale: this.$locale,
        recipients: this.recipients.map((r, i) => ({
          name: r.name,
          email: r.email,
          roleKey: r.roleKey || undefined,
          order: i + 1
        }))
      };

      if (this.mode === 'upload') {
        const data = new FormData();
        data.append('file', this.file);
        data.append('subject', payload.subject);
        data.append('message', payload.message || '');
        data.append('locale', payload.locale);
        data.append('recipients', JSON.stringify(payload.recipients));
        data.append('saveAsTemplate', String(this.saveAsTemplate));
        if (this.templateName) data.append('templateName', this.templateName);
        return api.post('/envelopes/upload', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (this.mode === 'blank') {
        payload.document = { source: 'blank', html: this.html };
        payload.saveAsTemplate = this.saveAsTemplate;
        if (this.templateName) payload.templateName = this.templateName;
      } else {
        payload.templateId = this.templateId;
      }
      return api.post('/envelopes', payload);
    },

    createOnly() {
      const problem = this.validate();
      if (problem) { this.errorMessage = problem; return; }
      if (this.saving) return;

      this.saving = true;
      this.errorMessage = '';
      this.create()
        .then((body) => {
          this.sentLinks = [];
          this.sentMessage = this.$t('newEnvelope.draftSaved', { id: body.envelope._id });
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    createAndSend() {
      const problem = this.validate();
      if (problem) { this.errorMessage = problem; return; }
      if (this.saving) return;

      this.saving = true;
      this.errorMessage = '';
      this.create()
        .then((body) => api.post('/envelopes/' + body.envelope._id + '/send'))
        .then((body) => {
          this.sentLinks = body.links;
          this.sentMessage = body.message;
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    reset() {
      Object.assign(this.$data, this.$options.data.call(this));
      this.loadTemplates();
    }
  }
};
</script>
<style scoped>
.new-page { max-width: 720px; margin: 0 auto; }

/* --- Bildirim --- */
.flash {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
  background: #fdeceb;
  color: #a8322c;
}

.flash-close {
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

/* --- Adım kartı --- */
.step {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 20px 22px 22px;
  margin-bottom: 14px;
}

.step-head {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 16px;
}

/* Numaralı rozet: form uzun, kullanıcı nerede olduğunu görsün */
.step-no {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #22223d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 700;
}

.step-head h3 {
  flex: 1 1 auto;
  font-size: 15px;
  font-weight: 700;
  color: #1e2233;
}

.step-body { margin-top: 4px; }

/* --- Kaynak seçimi --- */
.choices {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background: #fff;
  border: 1px solid #e2e6f0;
  border-radius: 11px;
  padding: 14px 14px 13px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}

.choice:hover { border-color: #c3cbdd; }

.choice.is-active {
  border-color: #00b89c;
  background: #f4fdfb;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.13);
}

.choice-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #f1f3fa;
  color: #79809a;
  font-size: 14px;
  margin-bottom: 6px;
}

.choice.is-active .choice-icon { background: #d9f6f0; color: #0b6e5f; }

.choice strong { font-size: 13.5px; color: #1e2233; }
.choice-hint { font-size: 12px; color: #98a0b5; line-height: 1.45; }

/* --- Seçilen şablon --- */
.picked {
  background: #f7f9fd;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 12px;
}

.picked-desc { font-size: 13px; color: #5a6180; }
.picked-roles { font-size: 12.5px; color: #8b93a8; margin-top: 7px; }

.tagline {
  display: inline-block;
  background: #fff;
  border: 1px solid #e2e6f0;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11.5px;
  font-weight: 600;
  color: #5a6180;
  margin-left: 5px;
}

/* --- Dosya --- */
.drop {
  border: 2px dashed #d5dae6;
  border-radius: 12px;
  padding: 26px 16px;
  text-align: center;
  cursor: pointer;
  background: #fbfcfe;
  transition: border-color 0.15s, background-color 0.15s;
}

.drop:hover { border-color: #00b89c; background: #f2fcfa; }
.drop.has-file { border-style: solid; border-color: #bfe8df; background: #f4fdfa; }

.hidden-input { display: none; }

.drop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: #eef1f8;
  color: #8b93a8;
  font-size: 18px;
  margin-bottom: 9px;
}

.drop.has-file .drop-icon { background: #e0f6f0; color: #0f8d78; }
.drop-text { font-size: 13.5px; color: #5a6180; }

/* --- Anahtar --- */
.switch {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 16px;
  font-size: 13.5px;
  color: #3c4257;
  cursor: pointer;
}

.switch input { position: absolute; opacity: 0; width: 0; height: 0; }

.switch-box {
  position: relative;
  flex: 0 0 36px;
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: #d9deea;
  transition: background-color 0.18s;
}

.switch-box::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(20, 20, 40, 0.24);
  transition: transform 0.18s;
}

.switch input:checked + .switch-box { background: #00b89c; }
.switch input:checked + .switch-box::after { transform: translateX(16px); }

/* --- Alıcılar --- */
.people { display: flex; flex-direction: column; gap: 9px; }

.person {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Sıra numarası imza sırasını anlatıyor: alttaki nota bakmadan da
   sıralı imzanın var olduğu anlaşılıyor */
.person-no {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: #f1f3fa;
  color: #79809a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.person-fields {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 8px;
}

.person-role {
  flex: 0 0 120px;
  display: flex;
  align-items: center;
  background: #f4f6fb;
  border-radius: 8px;
  padding: 0 11px;
  font-size: 12.5px;
  font-weight: 600;
  color: #5a6180;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --- Form --- */
.field { margin-bottom: 14px; }

.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
  margin-bottom: 5px;
}

.field label em { color: #c4413c; font-style: normal; }

.input,
.select-input {
  width: 100%;
  height: 40px;
  border: 1px solid #dde1ea;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #2b3146;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input.is-area,
.input.is-code {
  height: auto;
  padding: 10px 12px;
  line-height: 1.6;
  resize: vertical;
}

.input.is-code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
}

.input.is-small { height: 36px; font-size: 13px; margin-top: 9px; }

.input:focus,
.select-input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

.select-wrap { position: relative; display: block; }

.select-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 34px;
  cursor: pointer;
}

.select-wrap::after {
  content: "";
  position: absolute;
  right: 14px;
  top: 50%;
  margin-top: -2px;
  width: 0;
  height: 0;
  border-left: 4.5px solid transparent;
  border-right: 4.5px solid transparent;
  border-top: 5px solid #8b93a8;
  pointer-events: none;
}

.hint { font-size: 12.5px; color: #98a0b5; line-height: 1.6; }
.hint.mb { margin-bottom: 14px; }

.note {
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 12px;
  font-size: 12.5px;
  line-height: 1.6;
}

.note.is-warn { background: #fdf5e6; color: #7d5713; }

/* --- Eylem çubuğu --- */
.bar {
  display: flex;
  gap: 9px;
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 14px 18px;
}

.actions { display: flex; flex-wrap: wrap; gap: 9px; }
.actions.center { justify-content: center; margin-top: 20px; }

.btn-primary,
.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 9px;
  padding: 10px 18px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 3px 10px rgba(0, 168, 143, 0.28);
}

.btn-primary:hover { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }
.btn-primary.is-loading,
.btn-ghost.is-loading { opacity: 0.7; pointer-events: none; }

.btn-ghost { background: #fff; border: 1px solid #dde1ea; color: #3c4257; }
.btn-ghost:hover { border-color: #b9c0d2; }
.btn-ghost.is-mini { padding: 6px 11px; font-size: 12.5px; }

.icon-btn {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #98a0b5;
  cursor: pointer;
}

.icon-btn.is-danger:hover:not(:disabled) {
  border-color: #e0a9a5;
  color: #b63934;
  background: #fdf3f2;
}

.icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* --- Gönderildi --- */
.done {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 14px;
  padding: 34px 26px 28px;
  text-align: center;
}

.done-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%);
  color: #06302a;
  font-size: 22px;
  margin-bottom: 15px;
}

.done h2 { font-size: 19px; font-weight: 700; color: #1e2233; }
.done-lead { font-size: 13.5px; color: #79809a; margin: 6px 0 20px; }

.link-box {
  text-align: left;
  background: #f7f9fd;
  border: 1px solid #e6eaf3;
  border-radius: 11px;
  padding: 13px 14px;
  margin-bottom: 9px;
}

.link-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.link-who { display: flex; align-items: center; gap: 10px; min-width: 0; }

.row-mark {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #e5e9f6;
  color: #4a5175;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.link-who strong { display: block; font-size: 13.5px; color: #22283a; }
.row-sub { font-size: 12px; color: #8b93a8; }

.link-hint { font-size: 12px; color: #a66a12; margin-top: 8px; }

.link-text {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #0b6e5f;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  word-break: break-all;
}

.pill {
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.pill.is-ok { background: #e4f6ef; color: #0f6e5c; }
.pill.is-warn { background: #fdf2e2; color: #a66a12; }

@media screen and (max-width: 640px) {
  .choices { grid-template-columns: 1fr; }
  .person-fields { flex-direction: column; }
  .person-role { flex: 0 0 auto; height: 34px; }
  .bar { flex-direction: column; }
  .bar .btn-primary, .bar .btn-ghost { justify-content: center; }
}
</style>


// WEBPACK FOOTER //
// src/Components/NewEnvelope.vue